import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import '../styles/CameraSession.css';

const CameraSession = () => {
  const [isActive, setIsActive] = useState(false);
  const [sessionTimeLeft, setSessionTimeLeft] = useState(35); // 2 minutes in seconds
  const [preparationCountdown, setPreparationCountdown] = useState(0);
  const [captureCountdown, setCaptureCountdown] = useState(0);
  const [currentPhase, setCurrentPhase] = useState('waiting'); // waiting, preparation, capturing, processing
  const [capturedPhotos, setCapturedPhotos] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [showFlash, setShowFlash] = useState(false);
  const [autoStartCountdown, setAutoStartCountdown] = useState(30); // 30s pilih mode auto/manual
  const [isUploading, setIsUploading] = useState(false);
  const [captureMode, setCaptureMode] = useState(null); // 'auto' | 'manual' | null
  const [showRetakePrompt, setShowRetakePrompt] = useState(false);
  const [retakeCountdown, setRetakeCountdown] = useState(20);
  const [maxPhotos, setMaxPhotos] = useState(14);
  
  const videoRef = useRef(null);
  const navigate = useNavigate();
  const streamRef = useRef(null);
  const sessionIntervalRef = useRef(null);
  const preparationIntervalRef = useRef(null);
  const captureIntervalRef = useRef(null);
  const shotCountRef = useRef(0);
  const sessionIdRef = useRef(null);
  const isEndingRef = useRef(false);
  const capturedPhotosRef = useRef([]);
  const autoStartIntervalRef = useRef(null);
  const captureModeRef = useRef(null);
  const hasRetakenRef = useRef(false);
  
  // Video recording refs
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const isRecordingRef = useRef(false);

  // Load font and add animation styles
  useEffect(() => {
    // Load cursive font for the decorative title
    const fontLink = document.createElement('link');
    fontLink.rel = 'stylesheet';
    fontLink.href = 'https://fonts.googleapis.com/css2?family=Great+Vibes&display=swap';
    document.head.appendChild(fontLink);

    // Add wave animation keyframes
    const style = document.createElement('style');
    style.textContent = `
      @keyframes titleWave {
        0% { transform: translateY(0); }
        50% { transform: translateY(-8px); }
        100% { transform: translateY(0); }
      }
    `;
    document.head.appendChild(style);

    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
      if (document.head.contains(fontLink)) {
        document.head.removeChild(fontLink);
      }
    };
  }, []);

  // Check if frame is selected
  useEffect(() => {
    const currentSessionId = localStorage.getItem('currentSessionId');
    if (!currentSessionId) {
      alert('Tidak ada frame yang dipilih. Silakan pilih frame terlebih dahulu.');
      navigate('/frames');
      return;
    }
  }, [navigate]);

  // Countdown 30s untuk memilih mode auto / manual
  useEffect(() => {
    if (!isActive && !isUploading && captureMode === null) {
      autoStartIntervalRef.current = setInterval(() => {
        setAutoStartCountdown(prev => {
          if (prev <= 1) {
            clearInterval(autoStartIntervalRef.current);
            autoStartIntervalRef.current = null;
            // Jika user belum memilih mode, default ke Auto Snap
            if (captureModeRef.current === null) {
              captureModeRef.current = 'auto';
              setCaptureMode('auto');
              startSession('auto');
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (autoStartIntervalRef.current) {
        clearInterval(autoStartIntervalRef.current);
      }
    };
  }, [isActive, isUploading, captureMode]);

  // Initialize camera (bisa dipanggil ulang saat retake)
  const initializeCamera = async () => {
    try {
      // Stop stream yang sudah ada sebelumnya jika ada
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => {
          track.stop();
          console.log('Stopped existing track:', track.kind);
        });
        streamRef.current = null;
      }

      // Clear video srcObject dulu
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }

      console.log('Requesting camera access...');
      console.log('navigator.mediaDevices available:', !!navigator.mediaDevices);
      console.log('getUserMedia available:', !!navigator.mediaDevices?.getUserMedia);

      // Coba dengan beberapa constraint sebagai fallback
      let stream = null;
      const constraints = [
        // Try 1: Ideal resolution dengan facingMode
        {
          video: {
            width: { ideal: 1280 },
            height: { ideal: 720 },
            facingMode: 'user'
          },
          audio: false
        },
        // Try 2: Ideal resolution tanpa facingMode
        {
          video: {
            width: { ideal: 1280 },
            height: { ideal: 720 }
          },
          audio: false
        },
        // Try 3: Hanya video tanpa constraint spesifik
        {
          video: true,
          audio: false
        }
      ];

      // Coba setiap constraint sampai berhasil
      for (let i = 0; i < constraints.length; i++) {
        try {
          console.log(`Trying camera constraint ${i + 1}/${constraints.length}:`, JSON.stringify(constraints[i]));
          stream = await navigator.mediaDevices.getUserMedia(constraints[i]);
          console.log('✓ Camera access successful with constraint', i + 1);
          break;
        } catch (err) {
          console.warn(`Constraint ${i + 1} failed:`, err.name, err.message);
          if (i === constraints.length - 1) {
            // Jika semua constraint gagal, throw error terakhir
            throw err;
          }
        }
      }

      if (!stream) {
        throw new Error('Failed to get camera stream with any constraint');
      }
        
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        
        // Wait for video to be ready
        await new Promise((resolve, reject) => {
          if (!videoRef.current) {
            reject(new Error('Video element not available'));
            return;
          }
          
          const onLoadedMetadata = () => {
            console.log('Video metadata loaded');
            console.log('Video dimensions:', videoRef.current.videoWidth, 'x', videoRef.current.videoHeight);
            videoRef.current.removeEventListener('loadedmetadata', onLoadedMetadata);
            videoRef.current.removeEventListener('error', onError);
            resolve();
          };
          
          const onError = (e) => {
            console.error('Video element error:', e);
            videoRef.current.removeEventListener('loadedmetadata', onLoadedMetadata);
            videoRef.current.removeEventListener('error', onError);
            reject(new Error('Video element failed to load'));
          };
          
          videoRef.current.addEventListener('loadedmetadata', onLoadedMetadata);
          videoRef.current.addEventListener('error', onError);
          
          // Play video
          videoRef.current.play().catch(err => {
            console.warn('Video play() failed:', err);
            // Continue anyway, video might still work
          });
          
          // Timeout after 5 seconds
          setTimeout(() => {
            if (videoRef.current && videoRef.current.readyState === 0) {
              videoRef.current.removeEventListener('loadedmetadata', onLoadedMetadata);
              videoRef.current.removeEventListener('error', onError);
              reject(new Error('Video loading timeout'));
            }
          }, 5000);
        });
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      console.error('Error name:', error.name);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      
      // Stop any partial stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      
      let errorMessage = 'Tidak dapat mengakses kamera. ';
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        errorMessage += 'Pastikan izin kamera diberikan di Electron.';
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        errorMessage += 'Kamera tidak ditemukan.';
      } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
        errorMessage += 'Kamera sedang digunakan oleh aplikasi lain.';
      } else {
        errorMessage += `Error: ${error.message}`;
      }
      
      alert(errorMessage);
    }
  };

  useEffect(() => {
    initializeCamera();

    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  // Start video recording
  const startVideoRecording = () => {
    if (!streamRef.current || isRecordingRef.current) return;
    
    try {
      recordedChunksRef.current = [];
      isRecordingRef.current = true;
      
      const options = {
        mimeType: 'video/webm;codecs=vp9',
        videoBitsPerSecond: 2500000 // 2.5 Mbps
      };
      
      // Fallback to webm if vp9 is not supported
      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        options.mimeType = 'video/webm;codecs=vp8';
      }
      
      // Final fallback
      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        options.mimeType = 'video/webm';
      }
      
      mediaRecorderRef.current = new MediaRecorder(streamRef.current, options);
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorderRef.current.onstop = () => {
        isRecordingRef.current = false;
        console.log('Video recording stopped, chunks:', recordedChunksRef.current.length);
      };
      
      mediaRecorderRef.current.start();
      console.log('Video recording started');
      
    } catch (error) {
      console.error('Error starting video recording:', error);
      isRecordingRef.current = false;
    }
  };

  // Stop video recording and return blob
  const stopVideoRecording = () => {
    if (!mediaRecorderRef.current || !isRecordingRef.current) return null;
    
    try {
      mediaRecorderRef.current.stop();
      isRecordingRef.current = false;
      
      // Wait a bit for the ondataavailable event to fire
      return new Promise((resolve) => {
        setTimeout(() => {
          if (recordedChunksRef.current.length > 0) {
            const videoBlob = new Blob(recordedChunksRef.current, { type: 'video/webm' });
            console.log('Video blob created:', videoBlob.size, 'bytes');
            resolve(videoBlob);
          } else {
            console.warn('No video chunks recorded');
            resolve(null);
          }
        }, 100);
      });
    } catch (error) {
      console.error('Error stopping video recording:', error);
      isRecordingRef.current = false;
      return Promise.resolve(null);
    }
  };

  // Start session dengan mode tertentu
  const startSession = (mode) => {
    captureModeRef.current = mode;
    setCaptureMode(mode);

    // Hentikan countdown pilih mode jika masih berjalan
    if (autoStartIntervalRef.current) {
      clearInterval(autoStartIntervalRef.current);
      autoStartIntervalRef.current = null;
    }
    
    setIsActive(true);
    setSessionTimeLeft(hasRetakenRef.current ? 60 : 35);
    // Hanya reset foto di sesi pertama; retake menambah foto di atas yang lama
    if (!hasRetakenRef.current) {
    setCapturedPhotos([]);
      capturedPhotosRef.current = [];
    shotCountRef.current = 0;
      setMaxPhotos(14);
    }
    setCurrentPhase(mode === 'auto' ? 'preparation' : 'waiting');
    setPreparationCountdown(mode === 'auto' ? 3 : 0);
    
    // Get session ID from localStorage (created in Frames.js)
    const currentSessionId = localStorage.getItem('currentSessionId');
    if (!currentSessionId) {
      alert('Tidak ada session yang dipilih. Silakan pilih frame terlebih dahulu.');
      navigate('/frames');
      return;
    }
    
    console.log('Starting session with ID:', currentSessionId);
    setSessionId(currentSessionId);
    sessionIdRef.current = currentSessionId;
    
    console.log('Session initialized - sessionIdRef.current:', sessionIdRef.current);
    console.log('Session initialized - shotCountRef.current:', shotCountRef.current);
    
    // Start session timer
    sessionIntervalRef.current = setInterval(() => {
      setSessionTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(sessionIntervalRef.current);
          clearInterval(preparationIntervalRef.current);
          clearInterval(captureIntervalRef.current);
          setIsActive(false);
          setCurrentPhase('waiting');

          // Matikan kamera ketika prompt retake muncul
          if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
          }
          if (videoRef.current) {
            videoRef.current.srcObject = null;
          }

          // Sesi pertama: tampilkan opsi retake / no
          // Setelah retake (60s) selesai: langsung post
          if (!hasRetakenRef.current) {
            setRetakeCountdown(20);
            setShowRetakePrompt(true);
          } else {
          endSession();
          }

          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Mode auto: langsung mulai siklus pertama (prep + capture)
    if (mode === 'auto') {
    preparationIntervalRef.current = setInterval(() => {
      setPreparationCountdown(prev => {
        if (prev <= 1) {
          clearInterval(preparationIntervalRef.current);
          startCapturePhase();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    }
  };

  // Start capture phase
  const startCapturePhase = () => {
    setCurrentPhase('capturing');
    setCaptureCountdown(5);
    
    console.log('=== Starting Capture Phase ===');
    
    // Start video recording immediately when countdown starts
    startVideoRecording();
    
    // Start capture countdown
    captureIntervalRef.current = setInterval(() => {
      setCaptureCountdown(prev => {
        if (prev <= 1) {
          clearInterval(captureIntervalRef.current);
          capturePhoto();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Trigger flash effect
  const triggerFlash = () => {
    setShowFlash(true);
    setTimeout(() => {
      setShowFlash(false);
    }, 300); // Match the CSS animation duration
  };

  // Capture photo
  const capturePhoto = async () => {
    if (videoRef.current && streamRef.current) {
      // Trigger flash effect
      triggerFlash();
      
      // Stop video recording first
      const videoBlob = await stopVideoRecording();
      
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      
      // Ambil dimensi video; gunakan fallback jika 0 (kadang saat transisi)
      let vw = videoRef.current.videoWidth;
      let vh = videoRef.current.videoHeight;
      if (!vw || !vh) {
        console.warn('Video dimensions not ready, using fallback 1280x720');
        vw = 1280;
        vh = 720;
      }
      canvas.width = vw;
      canvas.height = vh;
      
      context.drawImage(videoRef.current, 0, 0, vw, vh);
      
      canvas.toBlob(async (photoBlob) => {
        if (!photoBlob) {
          console.error('Failed to create photo blob from canvas');
          processNextCycle();
          return;
        }

        const photoUrl = URL.createObjectURL(photoBlob);
        // Increment shot count first to get correct 1-based index consistently
        shotCountRef.current += 1;
        const shotIndex = shotCountRef.current;
        
        // Store photo and video for later upload and preview
        const captureData = {
          photo: {
            url: photoUrl,
            blob: photoBlob,
            filename: `${sessionIdRef.current}_${shotIndex}.jpg`
          },
          video: videoBlob ? {
            blob: videoBlob,
            filename: `${sessionIdRef.current}_video_${shotIndex}.webm`
          } : null
        };
        
        console.log(`Captured photo ${shotIndex}:`, captureData);
        setCapturedPhotos(prev => [...prev, captureData]);
        capturedPhotosRef.current.push(captureData);
        console.log(`Ref updated - Total captures in ref:`, capturedPhotosRef.current.length);
        
        // Process next cycle immediately
        processNextCycle();
      }, 'image/jpeg', 0.8);
    }
  };

  // Process next cycle
  const processNextCycle = () => {
    // Mode manual: setelah satu siklus selesai, tunggu tap user
    if (captureModeRef.current === 'manual') {
      setCurrentPhase('waiting');
      return;
    }

    if (sessionTimeLeft > 8) { // Need at least 8 seconds for next cycle (3 prep + 5 capture)
      setCurrentPhase('preparation');
      setPreparationCountdown(3);
      
      preparationIntervalRef.current = setInterval(() => {
        setPreparationCountdown(prev => {
          if (prev <= 1) {
            clearInterval(preparationIntervalRef.current);
            startCapturePhase();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      // Session is ending, don't start new cycle
      setCurrentPhase('waiting');
    }
  };

  // Untuk mode manual: user klik Let's Snap setiap ingin memulai siklus baru
  const triggerManualShot = () => {
    if (!isActive) return;
    if (captureModeRef.current !== 'manual') return;
    if (currentPhase !== 'waiting') return;

    setCurrentPhase('preparation');
    setPreparationCountdown(3);

    preparationIntervalRef.current = setInterval(() => {
      setPreparationCountdown(prev => {
        if (prev <= 1) {
          clearInterval(preparationIntervalRef.current);
          startCapturePhase();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Handler untuk prompt retake
  const handleRetake = () => {
    hasRetakenRef.current = true;
    setShowRetakePrompt(false);
    // Tambah kuota foto (14 -> 21)
    setMaxPhotos(prev => prev + 7);
    // Reset mode & countdown pemilihan mode, durasi tambahan 60s
    setCaptureMode(null);
    captureModeRef.current = null;
    setAutoStartCountdown(30);
    setSessionTimeLeft(60);
    setIsActive(false);

    // Reconnect kamera untuk sesi retake
    initializeCamera();
  };

  const handleNoRetake = () => {
    setShowRetakePrompt(false);
    endSession();
  };

  // Countdown 20s untuk memilih Retake / No
  useEffect(() => {
    if (!showRetakePrompt) return;
    setRetakeCountdown(20);

    const interval = setInterval(() => {
      setRetakeCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          // Jika user belum memilih dalam 20s, auto pilih "No"
          if (showRetakePrompt) {
            handleNoRetake();
      }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [showRetakePrompt]);

  // End session
  const endSession = async () => {
    setIsActive(false);
    setCurrentPhase('waiting');
    if (isEndingRef.current) return; // prevent double end
    isEndingRef.current = true;
    setIsUploading(true);

    clearInterval(sessionIntervalRef.current);
    clearInterval(preparationIntervalRef.current);
    clearInterval(captureIntervalRef.current);
    if (autoStartIntervalRef.current) {
      clearInterval(autoStartIntervalRef.current);
      autoStartIntervalRef.current = null;
    }

    // Stop any ongoing recording
    if (isRecordingRef.current) {
      await stopVideoRecording();
    }

    let effectiveId = sessionIdRef.current || sessionId;
    try {
      // Ensure session id exists
      if (!effectiveId) {
        effectiveId = 'session_' + new Date().toISOString().replace(/[-:.TZ]/g, '').slice(0, 14);
        sessionIdRef.current = effectiveId;
        console.log('Generated new session ID:', effectiveId);
      }
      // Post session data to backend
      // Cari frameId dari sessionFrames localStorage
      let gridValue = 6;
      try {
        const frames = JSON.parse(localStorage.getItem('sessionFrames') || '{}');
        const frameId = frames[effectiveId];
        if (frameId) gridValue = frameId;
      } catch{}
      // Ambil redeemed code dari localStorage jika ada
      const redeemedCode = localStorage.getItem('redeemedCode') || null;
      const sessionResponse = await fetch('https://api.laviopaperie.com/api/photo-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          indukname: effectiveId, 
          grid: gridValue, 
          layout: 'select-photo',
          codes: redeemedCode
        })
      });
      if (!sessionResponse.ok) throw new Error('Failed to save session data');
      await new Promise(resolve => setTimeout(resolve, 100));
      const currentCaptures = [...capturedPhotosRef.current];
      if (currentCaptures.length === 0) throw new Error('No captures captured during session');
      const formData = new FormData();
      currentCaptures.forEach((capture, index) => {
        if (capture.photo.blob && capture.photo.blob.size > 0) {
          const photoFile = new File([capture.photo.blob], capture.photo.filename, { type: 'image/jpeg' });
          formData.append('files', photoFile);
        }
        if (capture.video && capture.video.blob && capture.video.blob.size > 0) {
          const videoFile = new File([capture.video.blob], capture.video.filename, { type: 'video/webm' });
          formData.append('files', videoFile);
        }
      });
      Swal.fire({
        title: 'Please wait',
        text: 'wait your photos on progress upload',
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const uploadResponse = await fetch('https://api.laviopaperie.com/api/upload-session', {
        method: 'POST',
        body: formData
      });
      if (!uploadResponse.ok) {
        let errorText = '';
        try { errorText = await uploadResponse.text(); } catch {}
        throw new Error(`Failed to upload assets: ${uploadResponse.status} ${errorText}`);
      }
      await uploadResponse.json();
        // Setelah upload berhasil, langsung redirect ke Thank You Email;
        navigate(`/thank-you-email/${effectiveId}`);
        return;
    } catch (error) {
      console.error('Error ending session:', error);
      alert('Terjadi kesalahan saat menyimpan session. Silakan cek hasil session di halaman berikutnya.');
    } finally {
      Swal.close();
      setIsUploading(false);
      // Redirect ke halaman form email setelah session selesai
      navigate(`/thank-you-email/${effectiveId}`);
      // navigate(`/camera-results/${effectiveId}`); // Commented out - redirect ke email form instead
    }
  };

  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Download all photos
  const downloadPhotos = async () => {
    if (capturedPhotos.length === 0) return;
    
    try {
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();
      
      capturedPhotos.forEach((capture, index) => {
        zip.file(`photo_${index + 1}.jpg`, capture.photo.blob);
        if (capture.video) {
          zip.file(`video_${index + 1}.webm`, capture.video.blob);
        }
      });
      
      const content = await zip.generateAsync({ type: 'blob' });
      const downloadUrl = URL.createObjectURL(content);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${sessionId}_captures.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error downloading captures:', error);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearInterval(sessionIntervalRef.current);
      clearInterval(preparationIntervalRef.current);
      clearInterval(captureIntervalRef.current);
      clearInterval(autoStartIntervalRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      // Stop any ongoing recording
      if (isRecordingRef.current && mediaRecorderRef.current) {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  return (
    <div className="camera-session">
      <div className="camera-container">
        <div className="camera-header">
          <h1 style={{
            fontFamily: `'Great Vibes', cursive`,
            fontSize: '40px',
            lineHeight: 1.1,
            letterSpacing: '1px',
            color: '#ffffff',
            textShadow: '0 1px 0 rgba(86, 0, 47, 0.6)',
            margin: 0
          }}>
            {Array.from(' - Lavio Paperie - ').map((ch, i) => (
              <span
                key={i}
                style={{
                  display: 'inline-block',
                  animation: 'titleWave 2.2s ease-in-out infinite',
                  animationDelay: `${i * 0.06}s`,
                  willChange: 'transform'
                }}
              >
                 {ch === ' ' ? '\u00A0' : ch}
              </span>
            ))}
          </h1>
          <div className="session-timer">
            <span>Session Time: {formatTime(sessionTimeLeft)}</span>
            {isActive && (
              <span style={{
                marginLeft: '20px',
                fontSize: '16px',
                fontWeight: '600',
                color: '#ffffff',
                textShadow: '0 1px 2px rgba(0,0,0,0.3)'
              }}>
                Photos: {capturedPhotos.length}/{maxPhotos}
              </span>
            )}
          </div>
        </div>

        <div className="camera-view">
          {showRetakePrompt ? (
            <div className="retake-panel">
              <div className="retake-dialog">
                <h3>Time is up</h3>
                <p style={{marginTop:8}}>
                  Do you want to retake more photos?
                  <br />
                  <span style={{fontSize:'0.9rem',color:'#666'}}>
                    Auto "No" in {retakeCountdown}s
                  </span>
                </p>
                <div className="retake-buttons">
                  <button className="retake-button" onClick={handleRetake}>
                    Retake
                  </button>
                  <button className="retake-button secondary" onClick={handleNoRetake}>
                    No
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="camera-video"
          />
          
          {currentPhase === 'preparation' && preparationCountdown > 0 && (
            <div className="countdown-overlay preparation">
              <div className="countdown-text">
                <h2>Be Ready!</h2>
                <div className="countdown-number">{preparationCountdown}</div>
              </div>
            </div>
          )}
          
          {currentPhase === 'capturing' && captureCountdown > 0 && (
            <div className="countdown-text-overlay">
              <div className="countdown-text">
                {/* <h2>Foto dalam</h2> */}
                <div className="countdown-number">{captureCountdown}</div>
                {isRecordingRef.current && (
                  <div className="recording-indicator">
                    <span className="recording-dot"></span>
                    Recording...
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Flash overlay */}
          <div className={`flash-overlay ${showFlash ? 'active' : ''}`}></div>
            </>
          )}
        </div>

        {!isActive && (
          <div className="start-button-container" style={{backgroundColor:'#ffacb4'}}>
            <div style={{
              textAlign: 'center',
              marginBottom: '10px',
              fontSize: '14px',
              color: '#666',
              fontWeight: '500'
            }}>
              {captureMode === null && (
                <>
              {autoStartCountdown > 0 ? (
                    <span>
                      Choose mode in {autoStartCountdown}s<br />
                      (Auto Snap will start if no choice)
                    </span>
              ) : (
                    <span>Starting Auto Snap...</span>
                  )}
                </>
              )}
            </div>
            {captureMode === null && (
              <div style={{display:'flex',justifyContent:'center',gap:'12px',flexWrap:'wrap'}}>
            <button 
              className="start-button"
                  onClick={() => startSession('auto')}
            >
                  Auto Snap
                </button>
                <button
                  className="start-button manual"
                  onClick={() => startSession('manual')}
                >
                  Manual Tap
            </button>
              </div>
            )}
            {capturedPhotos.length > 0 && (
              <button 
                // className="download-button"
                // onClick={downloadPhotos}
              >
                {/* Download Semua Foto & Video */}
              </button>
            )}
          </div>
        )}

        {isActive && (
          <div className="session-info">
            {currentPhase === 'capturing' && isRecordingRef.current && (
              <div className="recording-status">
              </div>
            )}
            {captureMode === 'manual' && currentPhase === 'waiting' && !isUploading && (
              <button
                className="start-button manual"
                onClick={triggerManualShot}
              >
                Let's Snap!
              </button>
            )}
          </div>
        )}
      </div>

      <div className="photos-slideshow">
        <h3 style={{fontSize:'30px'}}>
          Snapped Pics: {capturedPhotos.length}/{maxPhotos}
        </h3>
        <div className="photos-container">
          {capturedPhotos.length > 0 ? (
            capturedPhotos.map((capture, index) => (
              <div key={index} className="photo-slide">
                <img src={capture.photo.url} alt={`Foto ${index + 1}`} />
                <div className="photo-number">{index + 1}</div>
                {capture.video && (
                  <div className="video-indicator">
                    <span className="video-icon">🎥</span>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: '#999',
              fontSize: '16px',
              textAlign: 'center',
              padding: '20px'
            }}>
              No pics yet,<br />
              Tap "Let's Snap!" to start!
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CameraSession; 